/**
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import { Client } from '../../shared/entities/client';
import ActionCreator from '../action-creator';

interface UpdateClientProps {
  id: string | null;
  clientData: Client;
}

export const ActionCreators = {
  // For requesting list of clients
  RequestClients: new ActionCreator<'RequestClients', any>('RequestClients'),
  ReceivedClientsSuccess: new ActionCreator<'ReceivedClientsSuccess', any>('ReceivedClientsSuccess'),
  ReceivedClientsError: new ActionCreator<'ReceivedClientsError', any>('ReceivedClientsError'),
  ClearReceivedClientsError: new ActionCreator<'ClearReceivedClientsError', any>('ClearReceivedClientsError'),

  // For requesting a specific client
  RequestCurrentClient: new ActionCreator<'RequestCurrentClient', string>('RequestCurrentClient'),
  ReceivedCurrentClientSuccess: new ActionCreator<'ReceivedCurrentClientSuccess', any>('ReceivedCurrentClientSuccess'),
  ReceivedCurrentClientError: new ActionCreator<'ReceivedCurrentClientError', any>('ReceivedCurrentClientError'),
  ClearReceivedCurrentClientError: new ActionCreator<'ClearReceivedCurrentClientError', any>(
    'ClearReceivedCurrentClientError',
  ),
  ClearCurrentClient: new ActionCreator<'ClearCurrentClient', any>('ClearCurrentClient'),

  // For creating a client
  ProvisionClient: new ActionCreator<'ProvisionClient', Client>('ProvisionClient'),
  ClientProvisioningSuccess: new ActionCreator<'ClientProvisioningSuccess', any>('ClientProvisioningSuccess'),
  ClientProvisioningError: new ActionCreator<'ClientProvisioningError', any>('ClientProvisioningError'),
  ClearClientProvisioningError: new ActionCreator<'ClearClientProvisioningError', any>('ClearClientProvisioningError'),

  // For deleting a client
  DeleteClient: new ActionCreator<'DeleteClient', string>('DeleteClient'),
  DeleteClientSuccess: new ActionCreator<'DeleteClientSuccess', any>('DeleteClientSuccess'),
  DeleteClientError: new ActionCreator<'DeleteClientError', any>('DeleteClientError'),
  ClearDeleteClientError: new ActionCreator<'ClearDeleteClientError', any>('ClearDeleteClientError'),

  // For updating a client
  UpdateClient: new ActionCreator<'UpdateClient', UpdateClientProps>('UpdateClient'),
  UpdateClientSuccess: new ActionCreator<'UpdateClientSuccess', any>('UpdateClientSuccess'),
  UpdateClientError: new ActionCreator<'UpdateClientError', any>('UpdateClientError'),
  ClearUpdateClientError: new ActionCreator<'ClearUpdateClientError', any>('ClearUpdateClientError'),
};

export type Action = typeof ActionCreators[keyof typeof ActionCreators];

export function requestClients() {
  return ActionCreators.RequestClients.create();
}
export function clearReceivedClientsError() {
  return ActionCreators.ClearReceivedClientsError.create();
}

export function requestCurrentClient(id: string) {
  return ActionCreators.RequestCurrentClient.create(id);
}
export function clearReceivedCurrentClientError() {
  return ActionCreators.ClearReceivedCurrentClientError.create();
}
export function clearCurrentClient() {
  return ActionCreators.ClearCurrentClient.create();
}

export function provisionClient(clientData: Client) {
  return ActionCreators.ProvisionClient.create(clientData);
}
export function clearClientProvisioningError() {
  return ActionCreators.ClearClientProvisioningError.create();
}

export function deleteClient(id: string) {
  return ActionCreators.DeleteClient.create(id);
}
export function clearDeleteClientError() {
  return ActionCreators.ClearDeleteClientError.create();
}

export function updateClient(id: string, clientData: Client) {
  return ActionCreators.UpdateClient.create({
    id: id,
    clientData: clientData,
  });
}
export function clearUpdateClientError() {
  return ActionCreators.ClearUpdateClientError.create();
}
